import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PiMagnifyingGlass, PiPencilSimpleLine } from 'react-icons/pi'

interface InboxSearchBarProps {
  value: string
  onChange: (value: string) => void
  onCompose?: () => void
}

/** Debounced search input with a Compose button. */
export function InboxSearchBar({
  value,
  onChange,
  onCompose,
}: InboxSearchBarProps) {
  const [localValue, setLocalValue] = useState(value)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setLocalValue(newValue)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        onChange(newValue)
      }, 300)
    },
    [onChange]
  )

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="flex items-center gap-2.5">
      <div className="relative flex h-9 flex-1 items-center rounded-md border border-neutral-300 bg-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.04)] focus-within:border-neutral-400">
        <PiMagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-brand-bright" />
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          placeholder="Search mail by company name, domain, sender"
          className="h-full w-full rounded-md bg-transparent pl-9 pr-3 text-sm tracking-[-0.2px] text-neutral-800 outline-none placeholder:text-neutral-500"
        />
      </div>
      <Ds2026ButtonProvider>
        <FbButton
          variant="primary"
          disabled={!onCompose}
          onClick={onCompose}
          className="!min-h-0 h-9 pl-2.5 pr-3.5 gap-1.5"
        >
          <PiPencilSimpleLine className="size-[18px]" />
          <span className="text-sm font-medium">Compose</span>
        </FbButton>
      </Ds2026ButtonProvider>
    </div>
  )
}
